import React, { useState, useEffect } from 'react';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const OnboardingTour = () => {
  const { user, updateUser } = useAuth();
  const [run, setRun] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user exists and hasn't completed the tour (0 or null)
    if (user && (user.onboarding_tour === 0 || user.onboarding_tour === null || user.onboarding_tour === undefined)) {
      setRun(true);
      // Optional: Ensure we start at dashboard or specific page?
      // navigate('/dashboard'); 
    }
  }, [user]);

  const steps = [
    {
      target: 'body',
      content: (
        <div className="text-left">
          <h2 className="text-xl font-bold mb-2 text-brand-primary">Willkommen im Team Collaboration Hub!</h2>
          <p>Wir freuen uns, dich an Bord zu haben. Lass uns dir kurz zeigen, wie du das Beste aus der Anwendung herausholst.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      route: '/dashboard'
    },
    {
      target: '[data-tour="nav-dashboard"]',
      content: 'Hier findest du das Dashboard mit den wichtigsten Kennzahlen und dem Status deines Teams auf einen Blick.',
      placement: 'right',
      route: '/dashboard'
    },
    {
      target: '[data-tour="nav-network"]',
      content: 'Im Netzwerk-Bereich siehst du das Wirkungsdiagramm, das die Zusammenhänge zwischen verschiedenen Faktoren und Zielen visualisiert.',
      placement: 'right',
      route: '/network'
    },
    {
      target: '[data-tour="nav-evaluations"]',
      content: 'Hier kannst du Bewertungen abgeben und Fragen beantworten, um den Status deines Teams aktuell zu halten.',
      placement: 'right',
      route: '/evaluations'
    },
    {
      target: '[data-tour="nav-handbook"]',
      content: 'Das Handbuch bietet dir wertvolle Verbesserungsvorschläge und Handlungsempfehlungen basierend auf den Ergebnissen.',
      placement: 'right',
      route: '/handbook'
    },
    ...(user?.teamId ? [
      {
        target: '[data-tour="nav-team-feedback"]',
        content: 'Tausche dich hier mit deinem Team aus, gib Feedback und fördere eine offene Kommunikationskultur.',
        placement: 'right',
        route: '/team-feedback'
      },
      {
        target: '[data-tour="nav-team-management"]',
        content: 'Verwalte hier deine Team-Einstellungen, Mitglieder und spezifische Elemente.',
        placement: 'right',
        route: '/team-management'
      }
    ] : []),
    {
      target: '[data-tour="header-profile"]',
      content: 'Hier oben kannst du dein Profil bearbeiten, Feedback zur App geben oder dich abmelden.',
      placement: 'bottom',
      route: '/profile'
    },
    {
      target: 'body',
      content: (
        <div className="text-left">
          <h2 className="text-xl font-bold mb-2 text-brand-primary">Viel Spaß!</h2>
          <p>Du bist nun bereit, loszulegen. Wir wünschen dir viel Erfolg bei der Zusammenarbeit!</p>
        </div>
      ),
      placement: 'center',
      route: '/dashboard'
    }
  ];

  const handleJoyrideCallback = async (data) => {
    const { status, type, action, index, step } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    // Wenn der Tooltip/Beacon aktiviert wird (z.B. durch Klick auf den blauen Kreis),
    // prüfen wir, ob wir auf der richtigen Seite sind, und navigieren ggf. dorthin.
    if (type === EVENTS.TOOLTIP) {
      if (step && step.route && location.pathname !== step.route) {
        navigate(step.route);
      }
    }

    if (finishedStatuses.includes(status)) {
      setRun(false);
      
      try {
        // Update user on server
        await apiService.updateUser(user.id, {
          onboarding_tour: 1
        });
        
        // Update local context
        updateUser({
          ...user,
          onboarding_tour: 1
        });
        
        // Navigate back to dashboard after finish
        navigate('/dashboard');
        
      } catch (error) {
        console.error("Failed to update onboarding status", error);
      }
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Check if we need to navigate for the NEXT step
      if (action === ACTIONS.NEXT) {
        const nextIndex = index + 1;
        if (steps[nextIndex]?.route) {
          navigate(steps[nextIndex].route);
        }
      } 
      // Check if we need to navigate for the PREVIOUS step
      else if (action === ACTIONS.PREV) {
        const prevIndex = index - 1;
        if (steps[prevIndex]?.route) {
          navigate(steps[prevIndex].route);
        }
      }
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showSkipButton={true}
      showProgress={true}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0056b3', // brand-primary
          zIndex: 10000,
        },
        buttonNext: {
          backgroundColor: '#0056b3',
          color: '#ffffff',
          borderRadius: '0.375rem',
          padding: '0.5rem 1rem',
          fontWeight: '500',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: '10px',
        },
        buttonSkip: {
          color: '#6b7280',
        },
        tooltip: {
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontWeight: 'bold',
          color: '#111827',
        },
      }}
      locale={{
        back: 'Zurück',
        close: 'Schließen',
        last: 'Fertig',
        next: 'Weiter',
        skip: 'Überspringen',
      }}
    />
  );
};

export default OnboardingTour;